/**
 * Navigation translations: sidebar groups, menu items, user menu, navbar
 *
 * Admin groups (TKT-2026-0854): MY WORK, AI, BILLING, ADMIN
 * Client groups: MY WORK, MY COMPANY
 * Talent: MY APPLICATION
 */
export const navigation = {
  en: {
    sidebar: {
      // Admin groups (TKT-2026-0854 splits Work into "My Work" (delivery)
      // and "Billing" (commercial: billing + service catalogue + proposals);
      // TKT-2026-0848 introduced AI as a separate conceptual section.
      // NOTE: the admin key stays `work` for backward compat; the rendered
      // label is "My Work" / "Mi Trabajo". Customer-side uses `myWork`
      // separately for its own "My Workspace" group.
      work: "My Work",
      ai: "AI",
      // TKT-2026-1703 — group renamed Billing → Commercial so it no longer
      // collides with the "Billing" page (menu.billing) inside it. Key stays
      // `billing` for backward compat.
      billing: "Commercial",
      strategy: "Strategy",
      admin: "Admin",
      // Client groups (new IA)
      myWork: "My Workspace",
      myCompany: "My Company",
      // Talent
      myApplication: "My Application",
      // Legacy (keep for backward compat during transition)
      overview: "Work",
      clientIntake: "Strategy",
      projectExecution: "Project Execution",
      deliverables: "Deliverables",
      internalTools: "Internal Tools",
      myPortal: "My Work",
    },
    menu: {
      dashboard: "Dashboard",
      dashboardDesc: "KPIs and quick actions",
      // Client overview
      overview: "Overview",
      overviewDesc: "Company information",
      // Client project (brand pipeline)
      project: "Project",
      projectDesc: "Brand development stages",
      // Work group
      clients: "Clients",
      clientsDesc: "Client management",
      companies: "Clients",
      companiesDesc: "Client management",
      projects: "Projects",
      projectsDesc: "All projects across clients",
      // TKT-2026-0703: `board` / `boardDesc` retired in favour of
      // `projectBoard` / `projectBoardDesc`. Sidebar now uses
      // `t.menu.projectBoard` exclusively.
      // TKT-2026-1620: label relabeled "Project Board" → "Tickets" for the
      // admin `/dashboard/tickets` surface; key name kept for callers.
      projectBoard: "Tickets",
      projectBoardDesc: "Manage company projects and track task progress",
      // TKT-2026-1657 — global cross-company Gantt at /dashboard/timeline/global
      // (admin-only). Distinct from the customer `clientTimeline` surface.
      globalTimeline: "Timeline",
      globalTimelineDesc: "Cross-company schedule of milestones, projects, and tickets",
      // Strategy group
      intake: "Intake",
      intakeDesc: "Review intake requests",
      serviceRequests: "Intake",
      serviceRequestsDesc: "Review intake requests",
      roadmap: "Roadmap",
      roadmapDesc: "Milestones and planning",
      planning: "Roadmap",
      planningDesc: "Milestones and planning",
      // TKT-2026-1620: label relabeled "Agent Queue" → "Agent Activity"
      // (route is /dashboard/agent-activity); key name kept for callers.
      agentQueue: "Agent Activity",
      agentQueueDesc: "AI task pipeline and status",
      // AI group (TKT-2026-0848). Memory is the admin label for the
      // notes module surfaced at /dashboard/notes; the customer-facing
      // label stays `notes` (forward-compat — customer sidebar doesn't
      // currently show notes). Terminal links to /dashboard/code (the
      // live tmux pane viewer). MCP is a placeholder entry — the page
      // itself ships in a separate ticket.
      memory: "Memory",
      memoryDesc: "AI session notes — strategy, decisions, research",
      notes: "Notes",
      notesDesc: "Markdown notes vault — strategy, research, scratch",
      mcp: "MCP",
      mcpDesc: "Model Context Protocol connectors and keys",
      terminal: "Terminal",
      terminalDesc: "Live Claude Code session viewer",
      activity: "Activity",
      activityDesc: "Log of all writes (humans + MCP agents)",
      // Admin group
      team: "Team",
      teamDesc: "Manage team members",
      // TKT-2026-0854 — relabeled to "Service Catalogue" now that the
      // item lives under the Billing group; the longer form reads more
      // naturally as a priced offerings catalogue. URL unchanged
      // (/dashboard/service-catalog).
      serviceCatalog: "Service Catalogue",
      serviceCatalogDesc: "Pricing, questionnaires, and services",
      talentApplications: "Talent",
      talentApplicationsDesc: "Review talent applications",
      settings: "Settings",
      settingsDesc: "Profile, design system, and CMS",
      // Client menu
      myProjects: "Projects",
      myProjectsDesc: "Your active projects",
      proposals: "Proposals",
      proposalsDesc: "Manage service proposals",
      approvals: "Approvals",
      approvalsDesc: "Pending approval requests",
      brand: "Brand",
      brandDesc: "Brand guidelines",
      socialMedia: "Social Media",
      socialMediaDesc: "Content calendar",
      // Talent menu
      application: "Application",
      applicationDesc: "View your application status",
      // Renamed from "Projects" to "Requests" to avoid confusion with brand pipeline
      myRequests: "Requests",
      myRequestsDesc: "Your service requests",
      pricing: "Service Catalog",
      pricingDesc: "Pricing, questionnaires, and services",
      questionnaires: "Questionnaires",
      questionnairesDesc: "Intake question bank",
      inbox: "Inbox",
      inboxDesc: "Notifications and updates",
      companyProfile: "Company Profile",
      companyProfileDesc: "Company information",
      brandAssets: "Brand",
      brandAssetsDesc: "Brand guidelines",
      designSystem: "Design System",
      designSystemDesc: "Brand assets & guidelines",
      websiteContent: "Website Content",
      websiteContentDesc: "Edit solariis.com homepage",
      pipeline: "Pipeline",
      pipelineDesc: "Your service request pipeline",
      clientOverview: "Overview",
      clientOverviewDesc: "Your company information",
      // TKT-2026-0703: customer-side personal kanban. Was previously
      // keyed `clientBoard`, which collided semantically with the
      // admin's read-only view of the same surface. Now `myBoard`
      // (label) and the new `clientBoard` key (admin tab) are
      // unambiguous.
      myBoard: "My Board",
      myBoardDesc: "Your personal task board",
      clientBoard: "Client Board",
      clientBoardDesc: "Read-only view of the client's operations board",
      clientTimeline: "Timeline",
      clientTimelineDesc: "Visual schedule of your tasks",
      solariisProgress: "Solariis Progress",
      solariisProgressDesc: "Track your requests with Solariis",
      // TKT-2026-0703 retired the `clientTickets` keys — replaced by
      // `solariisProgress` for the customer-side `/dashboard/tickets`
      // surface. Removed entirely in TKT-2026-0704 after a wide grep
      // confirmed zero callers.
      clientIntakeList: "Client Brief",
      clientIntakeListDesc: "Key information about your company and project",
      clientProposals: "Proposals",
      clientProposalsDesc: "Service proposals and agreements",
      clientDeliverables: "Deliverables",
      clientDeliverablesDesc: "Completed work and assets",
      billing: "Billing",
      billingDesc: "Invoices and payments",
      finance: "Finance",
      financeDesc: "Expenses and recurring items",
      clientBilling: "Billing",
      clientBillingDesc: "Your invoices and payment status",
    },
    user: {
      notSignedIn: "Not signed in",
      signIn: "Sign in",
      signOut: "Sign out",
    },
    navbar: {
      notifications: "Notifications",
      noNotifications: "No notifications",
      markAllRead: "Mark all as read",
      searchAdmin: "Search clients, projects, tickets...",
      searchClient: "Search your projects and tasks...",
      viewInternal: "Internal",
      viewClientPortal: "Client Portal",
    },
  },
  es: {
    sidebar: {
      // TKT-2026-0854 — admin "Work" relabeled to "Mi Trabajo"; new
      // Billing group added between AI and Admin.
      work: "Mi Trabajo",
      ai: "IA",
      // TKT-2026-1703 — grupo renombrado Facturación → Comercial para que no
      // choque con la página "Facturación" (menu.billing) dentro de él.
      billing: "Comercial",
      strategy: "Estrategia",
      admin: "Admin",
      myWork: "Mi Espacio",
      myCompany: "Mi Empresa",
      myApplication: "Mi Aplicación",
      // Legacy
      overview: "Trabajo",
      clientIntake: "Estrategia",
      projectExecution: "Ejecución",
      deliverables: "Entregables",
      internalTools: "Herramientas",
      myPortal: "Mi Trabajo",
    },
    menu: {
      dashboard: "Panel",
      dashboardDesc: "KPIs y acciones rápidas",
      overview: "General",
      overviewDesc: "Información de la empresa",
      project: "Proyecto",
      projectDesc: "Etapas de desarrollo de marca",
      clients: "Clientes",
      clientsDesc: "Gestión de clientes",
      companies: "Clientes",
      companiesDesc: "Gestión de clientes",
      projects: "Proyectos",
      projectsDesc: "Todos los proyectos",
      // TKT-2026-0703: `board` / `boardDesc` retirados — usar `projectBoard`.
      // TKT-2026-1620: etiqueta "Tablero de Proyectos" → "Tickets" para la
      // superficie admin `/dashboard/tickets`; nombre de clave sin cambios.
      projectBoard: "Tickets",
      projectBoardDesc: "Gestiona proyectos de empresas y rastrea el progreso",
      // TKT-2026-1657 — Gantt global multi-empresa en /dashboard/timeline/global
      // (solo admin). Distinto de la superficie de cliente `clientTimeline`.
      globalTimeline: "Cronograma",
      globalTimelineDesc: "Calendario global de hitos, proyectos y tickets",
      intake: "Solicitudes",
      intakeDesc: "Revisar solicitudes de intake",
      serviceRequests: "Solicitudes",
      serviceRequestsDesc: "Revisar solicitudes de intake",
      roadmap: "Hoja de Ruta",
      roadmapDesc: "Milestones y planificación",
      planning: "Hoja de Ruta",
      planningDesc: "Milestones y planificación",
      // TKT-2026-1620: etiqueta "Cola de Agentes" → "Actividad de Agentes"
      // (ruta /dashboard/agent-activity); nombre de clave sin cambios.
      agentQueue: "Actividad de Agentes",
      agentQueueDesc: "Pipeline de tareas IA y estado",
      // AI group (TKT-2026-0848).
      memory: "Memoria",
      memoryDesc: "Notas de sesión IA — estrategia, decisiones, investigación",
      notes: "Notas",
      notesDesc: "Vault de notas markdown — estrategia, investigación, ideas",
      mcp: "MCP",
      mcpDesc: "Conectores y claves Model Context Protocol",
      terminal: "Terminal",
      terminalDesc: "Visor de sesión Claude Code en vivo",
      activity: "Actividad",
      activityDesc: "Registro de escrituras (humanos + agentes MCP)",
      team: "Equipo",
      teamDesc: "Gestionar miembros del equipo",
      // TKT-2026-0854 — etiqueta cambiada a "Catálogo de Servicios"
      // ahora que vive bajo la sección Facturación.
      serviceCatalog: "Catálogo de Servicios",
      serviceCatalogDesc: "Precios, cuestionarios y servicios",
      talentApplications: "Talento",
      talentApplicationsDesc: "Revisar aplicaciones de talento",
      settings: "Configuración",
      settingsDesc: "Perfil, sistema de diseño y CMS",
      myProjects: "Proyectos",
      myProjectsDesc: "Tus proyectos activos",
      proposals: "Propuestas",
      proposalsDesc: "Gestionar propuestas de servicio",
      approvals: "Aprobaciones",
      approvalsDesc: "Solicitudes de aprobación pendientes",
      brand: "Marca",
      brandDesc: "Guías de marca",
      socialMedia: "Redes Sociales",
      socialMediaDesc: "Calendario de contenido",
      application: "Aplicación",
      applicationDesc: "Ver el estado de tu aplicación",
      myRequests: "Solicitudes",
      myRequestsDesc: "Tus solicitudes de servicio",
      pricing: "Catálogo",
      pricingDesc: "Precios, cuestionarios y servicios",
      questionnaires: "Cuestionarios",
      questionnairesDesc: "Banco de preguntas de intake",
      inbox: "Bandeja de Entrada",
      inboxDesc: "Notificaciones y actualizaciones",
      companyProfile: "Perfil de Empresa",
      companyProfileDesc: "Información de la empresa",
      brandAssets: "Marca",
      brandAssetsDesc: "Guías de marca",
      designSystem: "Sistema de Diseño",
      designSystemDesc: "Recursos y guías de marca",
      websiteContent: "Contenido Web",
      websiteContentDesc: "Editar solariis.com",
      pipeline: "Pipeline",
      pipelineDesc: "Tu pipeline de solicitudes",
      clientOverview: "General",
      clientOverviewDesc: "Información de tu empresa",
      // TKT-2026-0703: tablero personal del cliente.
      myBoard: "Mi Tablero",
      myBoardDesc: "Tu tablero personal de tareas",
      clientBoard: "Tablero del Cliente",
      clientBoardDesc: "Vista de solo lectura del tablero de operaciones del cliente",
      clientTimeline: "Cronograma",
      clientTimelineDesc: "Vista visual del calendario de tus tareas",
      solariisProgress: "Progreso Solariis",
      solariisProgressDesc: "Seguimiento de tus solicitudes con Solariis",
      // TKT-2026-0704: `clientTickets` retirado — sin callers tras la
      // migración a `solariisProgress` en TKT-2026-0703.
      clientIntakeList: "Brief del Cliente",
      clientIntakeListDesc: "Información clave sobre tu empresa y proyecto",
      clientProposals: "Propuestas",
      clientProposalsDesc: "Propuestas de servicio y acuerdos",
      clientDeliverables: "Entregables",
      clientDeliverablesDesc: "Trabajo completado y activos",
      billing: "Facturación",
      billingDesc: "Facturas y pagos",
      finance: "Finanzas",
      financeDesc: "Gastos y elementos recurrentes",
      clientBilling: "Facturación",
      clientBillingDesc: "Tus facturas y estado de pagos",
    },
    user: {
      notSignedIn: "Sin sesión",
      signIn: "Iniciar sesión",
      signOut: "Cerrar sesión",
    },
    navbar: {
      notifications: "Notificaciones",
      noNotifications: "Sin notificaciones",
      markAllRead: "Marcar todo como leído",
      searchAdmin: "Buscar clientes, proyectos, tickets...",
      searchClient: "Buscar tus proyectos y tareas...",
      viewInternal: "Interno",
      viewClientPortal: "Portal de Cliente",
    },
  },
};
